// src/components/ui/MovieIntroLoader.tsx
import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { commonVariants } from "@/types/animations";

interface MovieIntroLoaderProps {
  quote: string;
  minDuration?: number;
  onLoaded: () => void;
  isLoading: boolean;
}

const sentenceVariants = {
  hidden: { opacity: 1 },
  visible: {
    opacity: 1,
    transition: {
      delay: 0.3, // Delay reducido para empezar más rápido
      staggerChildren: 0.05, // Animación más fluida
    },
  },
};

// Usar variants predefinidos y tipados correctamente
const letterVariants = commonVariants.letterFade;

const loaderContainerVariants = {
  initial: {
    opacity: 1,
  },
  exit: {
    opacity: 0,
    transition: {
      duration: 1.2, // Fade out más lento para evitar parpadeo
      ease: "easeInOut",
    },
  },
} as const;

const MovieIntroLoader: React.FC<MovieIntroLoaderProps> = ({
  quote,
  minDuration = 6000,
  onLoaded,
  isLoading,
}) => {
  const [isMinTimeElapsed, setIsMinTimeElapsed] = useState(false);
  const [isAnimationComplete, setIsAnimationComplete] = useState(false);
  const minTimeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const loadedRef = useRef(false);

  // Detectar móvil INMEDIATAMENTE en SSR/cliente - usar detección sincrónica
  const isMobile = typeof window !== "undefined" && window.innerWidth < 768;

  // En móvil, llamar onLoaded INMEDIATAMENTE sin ningún delay
  useEffect(() => {
    if (isMobile && isLoading && !loadedRef.current) {
      loadedRef.current = true;
      onLoaded();
    }
  }, [isMobile, isLoading, onLoaded]);

  useEffect(() => {
    // Solo ejecutar lógica del loader en desktop
    if (isMobile) return;

    if (isLoading && !isMinTimeElapsed) {
      if (minTimeTimeoutRef.current) clearTimeout(minTimeTimeoutRef.current);

      minTimeTimeoutRef.current = setTimeout(() => {
        setIsMinTimeElapsed(true);

        if (isAnimationComplete && !loadedRef.current) {
          loadedRef.current = true;
          onLoaded();
        }
      }, minDuration);
    }

    return () => {
      if (minTimeTimeoutRef.current) clearTimeout(minTimeTimeoutRef.current);
    };
  }, [isMobile, isLoading, minDuration, isAnimationComplete, onLoaded]);

  // Cuando la animación de texto se completa
  const handleTextAnimationComplete = () => {
    if (isMobile) return;
    setIsAnimationComplete(true);
    // Si ya pasó el tiempo mínimo, notificar que está listo
    if (isMinTimeElapsed && isLoading && !loadedRef.current) {
      loadedRef.current = true;
      onLoaded();
    }
  };

  useEffect(() => {
    if (!isLoading) {
      loadedRef.current = false;
      setIsAnimationComplete(false);
      setIsMinTimeElapsed(false);
    }
  }, [isLoading]);

  useEffect(() => {
    // Solo ejecutar timeout de seguridad en desktop
    if (isMobile) return;

    const forceFinishTimeout = setTimeout(() => {
      if (isLoading && !loadedRef.current) {
        loadedRef.current = true;
        onLoaded();
      }
    }, minDuration + 2000); // Timeout más conservador

    return () => clearTimeout(forceFinishTimeout);
  }, [isMobile, isLoading, minDuration, onLoaded]);

  // En móvil no renderizar nada
  if (isMobile) {
    return null;
  }

  return (
    <AnimatePresence>
      {isLoading && (
        <motion.div
          key="introLoaderContainer"
          className="fixed inset-0 flex flex-col items-center justify-center bg-black text-gray-200 p-4 sm:p-8 overflow-hidden"
          style={{
            zIndex: 9999, // Z-index muy alto para asegurar visibilidad
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: "100vw",
            height: "100vh",
            minHeight: "100vh",
            minWidth: "100vw",
          }}
          variants={loaderContainerVariants}
          initial="initial"
          exit="exit"
        >
          <motion.div
            className="absolute inset-0 z-[-1] bg-gradient-to-br from-black via-emerald-900/30 to-black"
            style={{
              backgroundImage: `
                radial-gradient(ellipse at 30% 20%, rgba(0, 128, 90, 0.15) 0%, transparent 50%),
                radial-gradient(ellipse at 70% 80%, rgba(10, 150, 120, 0.1) 0%, transparent 50%)
              `,
              backgroundSize: "200% 200%",
            }}
            animate={{
              backgroundPosition: [
                "0% 0%",
                "10% 15%",
                "0% 30%",
                "-10% 15%",
                "0% 0%",
              ],
            }}
            transition={{
              duration: isMobile ? 30 : 45, // Animación más rápida en móvil
              repeat: Infinity,
              ease: "linear",
            }}
          />
          <div className="text-center max-w-4xl px-4">
            <motion.p
              className={`font-bruno-ace italic mb-6 ${
                isMobile
                  ? "text-2.2xl sm:text-3.2xl" // Texto más pequeño en móvil
                  : "text-2.2xl sm:text-3.2xl md:text-6xl"
              }`}
              variants={sentenceVariants}
              initial="hidden"
              animate="visible"
              onAnimationComplete={handleTextAnimationComplete}
              style={{
                fontFamily: '"Bruno Ace", sans-serif',
                lineHeight: isMobile ? "1.3" : "1.2", // Mejor espaciado en móvil
              }}
            >
              {quote.split("").map((char, index) => (
                <motion.span
                  key={`${char}-${index}`}
                  variants={letterVariants}
                  style={{ display: "inline-block" }}
                >
                  {char === " " ? "\u00A0" : char}
                </motion.span>
              ))}
            </motion.p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MovieIntroLoader;
